import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  RuntimeConfig,
  parseConfigFromUrl,
  getConfigSearchParams,
  isValidUrl
} from '../config/runtimeConfig';

interface ConfigContextType {
  config: RuntimeConfig | null;
  isLoading: boolean;
  error: string | null;
  missingParams: string[];
  /** Get the search params string to preserve config in navigation */
  getSearchParams: () => string;
}

const ConfigContext = createContext<ConfigContextType | null>(null);

interface ConfigProviderProps {
  children: ReactNode;
}

export function ConfigProvider({ children }: ConfigProviderProps) {
  const [config, setConfig] = useState<RuntimeConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [missingParams, setMissingParams] = useState<string[]>([]);

  // Load configuration from URL on mount
  useEffect(() => {
    const result = parseConfigFromUrl();

    if (result.error || !result.config) {
      console.error('Config error:', result.error);
      setError(result.error);
      setMissingParams(result.missingParams);
      setConfig(null);
      setIsLoading(false);
      return;
    }

    // Validate URLs
    if (!isValidUrl(result.config.apiBaseUrl, ['http:', 'https:'])) {
      setError(`Invalid apiUrl format: ${result.config.apiBaseUrl}`);
      setConfig(null);
      setIsLoading(false);
      return;
    }
    if (!isValidUrl(result.config.wsBaseUrl, ['ws:', 'wss:'])) {
      setError(`Invalid wsUrl format: ${result.config.wsBaseUrl}`);
      setConfig(null);
      setIsLoading(false);
      return;
    }

    setConfig(result.config);
    console.log('Config loaded:', result.config);
    setIsLoading(false);
  }, []);

  const value: ConfigContextType = {
    config,
    isLoading,
    error,
    missingParams,
    getSearchParams: getConfigSearchParams
  };

  return (
    <ConfigContext.Provider value={value}>
      {children}
    </ConfigContext.Provider>
  );
}

export function useConfig(): ConfigContextType {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
}

export default ConfigContext;
